import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import { ErrorFactory, logError } from "../lib/error-handling";

export type SupportEventType = "error" | "warning" | "info" | "performance";
export type SupportEventSeverity = "low" | "medium" | "high" | "critical";

export interface SupportEventDetails {
  component?: string;
  action?: string;
  params?: Record<string, any>;
  state?: Record<string, any>;
  stack?: string;
  userId?: string;
  userEmail?: string;
}

export type GhostResolutionType = "kb" | "assistant" | "auto";

export interface GhostResolutionDetails {
  topic: string;
  resolutionType: GhostResolutionType;
  userQuery?: string;
  responseTime?: number;
  satisfaction?: "helpful" | "not_helpful";
}

export interface SupportEventInput {
  eventType: SupportEventType;
  eventCode: string;
  message: string;
  severity: SupportEventSeverity;
  details?: SupportEventDetails;
}

/**
 * Service de logging pour les événements support
 * Centralise la capture d'erreurs et événements système
 */
export class SupportEventLogger {
  private static instance: SupportEventLogger;
  private sessionId: string;
  private pendingEvents: SupportEventInput[] = [];
  private flushInterval: number | null = null;
  private isOnline = navigator.onLine;

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.setupOfflineHandling();
    this.startPeriodicFlush();
  }

  public static getInstance(): SupportEventLogger {
    if (!SupportEventLogger.instance) {
      SupportEventLogger.instance = new SupportEventLogger();
    }
    return SupportEventLogger.instance;
  }

  /**
   * Logue un événement de support
   */
  public async logEvent(event: SupportEventInput): Promise<void> {
    try {
      // Ajouter aux événements en attente
      this.pendingEvents.push(event);

      // Flush immédiat pour les erreurs critiques
      if (event.severity === "critical" || event.eventType === "error") {
        await this.flushEvents();
      }
    } catch (error) {
      // Fallback: logger local si Supabase échoue
      logger.error("Failed to log support event:", error as any);
    }
  }

  /**
   * Logue une erreur avec capture automatique du contexte
   */
  public async logError(
    error: Error,
    eventCode: string,
    context?: Omit<SupportEventDetails, "stack">,
  ): Promise<void> {
    await this.logEvent({
      eventType: "error",
      eventCode,
      message: error.message,
      severity: this.determineSeverity(error),
      details: {
        ...context,
        stack: error.stack,
      },
    });
  }

  /**
   * Logue un événement performance
   */
  public async logPerformance(
    metric: string,
    value: number,
    context?: SupportEventDetails,
  ): Promise<void> {
    await this.logEvent({
      eventType: "performance",
      eventCode: `perf_${metric}`,
      message: `Performance metric: ${metric} = ${value}ms`,
      severity: value > 5000 ? "high" : value > 2000 ? "medium" : "low",
      details: {
        ...context,
        params: { metric, value },
      },
    });
  }

  /**
   * Logue un événement utilisateur
   */
  public async logUserAction(
    action: string,
    component: string,
    details?: Record<string, any>,
  ): Promise<void> {
    await this.logEvent({
      eventType: "info",
      eventCode: `user_${action}`,
      message: `User action: ${action} in ${component}`,
      severity: "low",
      details: {
        action,
        component,
        params: details,
      },
    });
  }

  /**
   * Logue une résolution fantôme (résolution sans création de ticket)
   */
  public async logGhostResolution(
    topic: string,
    resolutionType: GhostResolutionDetails["resolutionType"],
    context?: Partial<GhostResolutionDetails>,
  ): Promise<void> {
    await this.logEvent({
      eventType: "info",
      eventCode: "ghost_resolution",
      message: `Résolution sans ticket : ${topic}`,
      severity: "low",
      details: {
        component: "SupportWidget",
        action: "ghost_resolution",
        params: {
          topic,
          resolutionType,
          timestamp: new Date().toISOString(),
          ...context,
        },
      },
    });
  }

  /**
   * Flush les événements en attente vers Supabase
   */
  private async flushEvents(): Promise<void> {
    if (this.pendingEvents.length === 0 || !this.isOnline) {
      return;
    }

    const eventsToFlush = [...this.pendingEvents];
    this.pendingEvents = [];

    try {
      const { error } = await supabase.functions.invoke("log-support-events", {
        body: { events: eventsToFlush, sessionId: this.sessionId },
      });

      if (error) {
        throw error;
      }

      logger.info(`Flushed ${eventsToFlush.length} support events`);
    } catch (error) {
      // Remettre les événements en attente en cas d'échec
      this.pendingEvents.unshift(...eventsToFlush);
      logError(
        ErrorFactory.api("Failed to flush support events", error as any),
        {
          component: "SupportEventLogger",
          operation: "flushEvents",
          error: error as any,
        },
      );
    }
  }

  /**
   * Détermine la sévérité d'une erreur
   */
  private determineSeverity(error: Error): SupportEventSeverity {
    // Erreurs réseau
    if (error.message.includes("fetch") || error.message.includes("network")) {
      return "medium";
    }

    // Erreurs d'authentification
    if (
      error.message.includes("auth") ||
      error.message.includes("unauthorized")
    ) {
      return "high";
    }

    // Erreurs critiques (base de données, service down)
    if (
      error.message.includes("database") ||
      error.message.includes("connection") ||
      error.message.includes("timeout")
    ) {
      return "critical";
    }

    // Par défaut, medium
    return "medium";
  }

  /**
   * Génère un ID de session unique
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Configure la gestion offline/online
   */
  private setupOfflineHandling(): void {
    window.addEventListener("online", () => {
      this.isOnline = true;
      this.flushEvents(); // Flush dès qu'on est de retour online
    });

    window.addEventListener("offline", () => {
      this.isOnline = false;
    });
  }

  /**
   * Démarre le flush périodique
   */
  private startPeriodicFlush(): void {
    // Flush toutes les 30 secondes
    this.flushInterval = window.setInterval(() => {
      this.flushEvents();
    }, 30000);
  }

  /**
   * Nettoyage
   */
  public destroy(): void {
    if (this.flushInterval) {
      window.clearInterval(this.flushInterval);
    }
    this.flushEvents(); // Flush final
  }
}

// Instance singleton
export const supportEventLogger = SupportEventLogger.getInstance();

// Fonctions utilitaires pour usage courant
export const logSupportError = (
  error: Error,
  eventCode: string,
  context?: Omit<SupportEventDetails, "stack">,
) => supportEventLogger.logError(error, eventCode, context);

export const logSupportPerformance = (
  metric: string,
  value: number,
  context?: SupportEventDetails,
) => supportEventLogger.logPerformance(metric, value, context);

export const logSupportUserAction = (
  action: string,
  component: string,
  details?: Record<string, any>,
) => supportEventLogger.logUserAction(action, component, details);

export const logGhostResolution = (
  topic: string,
  resolutionType: GhostResolutionDetails["resolutionType"],
  context?: Partial<GhostResolutionDetails>,
) => supportEventLogger.logGhostResolution(topic, resolutionType, context);

// Hook React pour intégration facile
export const useSupportLogger = () => {
  return {
    logError: logSupportError,
    logPerformance: logSupportPerformance,
    logUserAction: logSupportUserAction,
    logGhostResolution: logGhostResolution,
    logEvent: supportEventLogger.logEvent.bind(supportEventLogger),
  };
};
